import { getEnvironment } from '@uipath/portal-shell-util';

import {
    AccountType,
    BuiltInGroups,
    LanguageCodes,
    PendoApiKey,
} from '../../constants/constants';
import pendoStyles from '../../constants/pendo.css';
import {
    IPendoAccountOptions,
    IPendoOptions,
    IPendoVisitorOptions,
} from '../../models/pendo';
import store from '../../store/store';
import { accountAndTenantsStore } from '../accountAndTenants/accountAndTenants';
import { EventHandlersInstanceResolver } from '../events/eventHandlersInstanceResolver';
import GlobalEventEmitter from '../events/globalEventEmitters';
import { removeObjectUndefinedProperties } from '../helper';
import { getLanguage } from '../languageHelper';
import Logger from '../logger/logger';
import { sessionStore } from '../session/sessionStore';
import { getSpaceName } from '../urlHelper';

export default class Pendo {
    isInitialized: boolean = false;
    pendoInstance: any;
    private apiKey: string;

    // We cache the current visitor and account metadata so that we can detect changes and update the pendo instance when necessary
    // Updating too frequently can cause pendo guides to flash
    private currentVisitorMetadata: any = {};
    private currentAccountMetadata: any = {};

    constructor(pendoApiKey: string = Pendo.getApiKey()) {
        this.apiKey = pendoApiKey;

        if (this.apiKey) {
            try {
                // @ts-ignore
                if (!window.pendo) {
                    Pendo.mountScript(this.apiKey);
                    Pendo.addOverrideStyles();
                }

                // @ts-ignore
                this.pendoInstance = window.pendo;
            } catch (e) {
                Logger.error('pendo', `Error mounting pendo script and/or styles: ${e}`);
            }
        } else {
            Logger.error('pendo', 'Missing api key', { printToConsoleOnly: true });
        }
    }

    initializeFromStore(
    ) {
        this.initialize({
            visitor: Pendo.mapStoreToVisitorProps(),
            account: Pendo.mapStoreToAccountProps(),
        });
    }

    initialize(options: IPendoOptions) {
        if (!this.apiKey || !this.pendoInstance || this.isInitialized) {
            Logger.debug('pendo', `not initializing: key=${this.apiKey} pendo=${this.pendoInstance} initialized=${this.isInitialized}`);
            return;
        }

        try {
            const visitor = this.getVisitorOptions(options.visitor);
            const account = this.getAccountOptions(options.account);
            this.currentVisitorMetadata = visitor;
            this.currentAccountMetadata = account;

            Logger.debug('pendo', 'calling Pendo initialize');
            this.pendoInstance.initialize({
                visitor,
                account,
                excludeAllText: true,
                events: { ready: () => Pendo.onReadyCallback(this) },
            });
        } catch (exception) {
            Logger.error('pendo', 'Error initializing Pendo, exception: ' + exception, { stack: exception.stack });
        }
    }

    updateOptionsFromStore() {
        if (!this.isInitialized) {
            Logger.debug('pendo', 'not updating options, pendo DNE or not initialized');
            return;
        }

        Logger.debug('pendo', 'update pendo options from store');
        this.updateOptions({
            visitor: Pendo.mapStoreToVisitorProps(),
            account: Pendo.mapStoreToAccountProps(),
        });
    }

    updateOptions(options: Omit<IPendoOptions, 'visitor.userId' | 'account.organizationId'>) {
        if (!this.isInitialized) {
            Logger.debug('pendo', 'not updating options, pendo DNE or not initialized');
            return;
        }

        // Get metadata and check if there are any changes
        let metadata: any = undefined;
        const visitor = this.getVisitorOptions(options.visitor);
        if (visitor) {
            metadata = { visitor };
            this.currentVisitorMetadata = {
                ...this.currentVisitorMetadata,
                ...visitor,
            };
        }

        const account = this.getAccountOptions(options.account);
        if (account) {
            metadata = {
                ...metadata,
                account,
            };
            this.currentAccountMetadata = {
                ...this.currentAccountMetadata,
                ...account,
            };
        }

        // Only update if there are changes or the guides will flash
        if (metadata) {
            Logger.debug('pendo', 'update pendo options');
            this.pendoInstance.updateOptions(metadata);
        } else {
            Logger.debug('pendo', 'skipping; no change in options');
        }
    }

    updateVisitorOptions(visitorOptions: Omit<IPendoVisitorOptions, 'userId'>) {
        if (!this.isInitialized) {
            Logger.debug('pendo', 'not updating options, pendo DNE or not initialized');
            return;
        }

        // Get metadata and only update if there are changes
        const visitor = this.getVisitorOptions(visitorOptions);
        if (visitor) {
            Logger.debug('pendo', 'update pendo visitor options', { object: visitorOptions });
            this.currentVisitorMetadata = {
                ...this.currentVisitorMetadata,
                ...visitor,
            };
            this.pendoInstance.updateOptions({ visitor });
        } else {
            Logger.debug('pendo', 'skipping; no change in visitor options');
        }
    }

    updateAccountOptions(accountOptions: Omit<IPendoAccountOptions, 'organizationId'>) {
        if (!this.isInitialized) {
            Logger.debug('pendo', 'not updating options, pendo DNE or not initialized');
            return;
        }

        // Get metadata and only update if there are changes
        const account = this.getAccountOptions(accountOptions);
        if (account) {
            Logger.debug('pendo', 'update pendo account options', { object: accountOptions });
            this.currentAccountMetadata = {
                ...this.currentAccountMetadata,
                ...account,
            };
            this.pendoInstance.updateOptions({ account });
        } else {
            Logger.debug('pendo', 'skipping; no change in account options');
        }
    }

    openGuide(guideId: string) {
        if (!this.isInitialized) {
            Logger.debug('pendo', 'not opening guide, pendo DNE or not initialized');
            return;
        }

        Logger.debug('pendo', `showing guide ${guideId}`);
        this.pendoInstance.showGuideById(guideId);
    }

    getCurrentMetadata(): any {
        return {
            visitor: this.currentVisitorMetadata,
            account: this.currentAccountMetadata,
        };
    }

    private static getApiKey(): string {
        const spaceName = getSpaceName();
        const env = getEnvironment();
        if (env === 'alp' || env === 'stg' || env === 'prd') {
            return PendoApiKey[spaceName] || '';
        }
        return '';
    }

    private static mountScript(pendoApiKey: string) {
        (function(p, e, n, d, o) {
            // @ts-ignore
            let v, w, x, y, z; o = p[d] = p[d] || {}; o._q = [];
            // eslint-disable-next-line prefer-const
            v = [ 'initialize', 'identify', 'updateOptions', 'pageLoad', 'track' ];
            for (w = 0, x = v.length;w < x; ++w) {
                (function(m) {
                    // @ts-ignore
                    o[m] = o[m] || function() {
                        // @ts-ignore
                        // eslint-disable-next-line prefer-rest-params
                        o._q[m === v[0] ? 'unshift' : 'push']([ m ].concat([].slice.call(arguments, 0)));
                    };
                })(v[w]);
            }
            // eslint-disable-next-line prefer-const
            y = e.createElement(n); y.async = !0; y.src = 'https://content.usage.uipath.com/agent/static/' + pendoApiKey + '/pendo.js';
            // eslint-disable-next-line prefer-const
            z = e.getElementsByTagName(n)[0]; z.parentNode.insertBefore(y, z);
        })(window, document, 'script', 'pendo');
    }

    private static addOverrideStyles() {
        Logger.debug('pendo', 'adding style element to header');
        const documentHead = document.head;
        const styleElement = document.createElement('style');
        styleElement.appendChild(document.createTextNode(pendoStyles));
        documentHead.appendChild(styleElement);
    }

    private static mapStoreToVisitorProps(
    ): IPendoVisitorOptions {
        const urlTenantName = accountAndTenantsStore.get('currentTenant');
        const activeTenantId = urlTenantName
            ? accountAndTenantsStore.state.tenants?.find(tenant => tenant.name?.toLowerCase() === urlTenantName.toLowerCase())?.id
            : undefined;

        return {
            userId: sessionStore.get('userId'),
            language: getLanguage(store) as LanguageCodes || 'en',
            activeTenantId,
            userGroups: accountAndTenantsStore.state.userGroups as BuiltInGroups[],
        };
    }

    private static mapStoreToAccountProps(): IPendoAccountOptions {
        const provisionedServices = [ ...new Set([
            ...(accountAndTenantsStore?.state.organization?.accountServices ?? []),
            ...(accountAndTenantsStore?.state.tenants?.flatMap(tenant => tenant.enabledServices) ?? []),
            'home',
            'help',
        ]) ];

        return {
            organizationId: sessionStore.get('organizationId'),
            accountType: accountAndTenantsStore.state.organization?.accountType as AccountType | undefined,
            provisionedServices,
        };
    }

    // The delegate has to be passed in or the instance's isInitialized field doesn't get set properly
    private static onReadyCallback(delegate: Pendo) {
        delegate.isInitialized = true;

        Logger.debug('pendo', 'ready, loading guides');
        delegate.pendoInstance.loadGuides();

        Logger.debug('pendo', `Pendo initialized successfully: ${delegate.isInitialized}`);

        const pendoEvent = { pendoOptions: delegate.getCurrentMetadata() };
        GlobalEventEmitter.emitPendoInitialized(pendoEvent);
        EventHandlersInstanceResolver.Instance?.onPendoInitialized?.(pendoEvent);
        Logger.debug([ 'pendo', 'event' ], 'init emitted');
    }

    private getVisitorOptions({
        userId, language, activeTenantId, userGroups, customOptions,
    }: Partial<IPendoVisitorOptions>) {
        Logger.debug('pendo', 'getting Pendo visitor options');
        const visitorOptions = {
            ...customOptions,
            id: userId,
            language: language?.replace('-', '_').toLowerCase(),
            language_uipath: language?.toLowerCase(),
            groups: userGroups,
            admin: userGroups?.includes(BuiltInGroups.Admin),
            activeTenantId,
        };
        const filtered = removeObjectUndefinedProperties(visitorOptions);
        return this.hasNotUpdated(this.currentVisitorMetadata, filtered) ? undefined : filtered;
    }

    private getAccountOptions({
        organizationId, accountType, provisionedServices, customOptions,
    }: Partial<IPendoAccountOptions>) {
        Logger.debug('pendo', 'getting Pendo account options');
        const accountOptions = {
            ...customOptions,
            id: organizationId,
            accountType,
            provisionedServices,
        };
        const filtered = removeObjectUndefinedProperties(accountOptions);
        return this.hasNotUpdated(this.currentAccountMetadata, filtered) ? undefined : filtered;
    }

    private hasNotUpdated(
        options: IPendoVisitorOptions | IPendoAccountOptions,
        subset: Partial<IPendoVisitorOptions> | Partial<IPendoAccountOptions>,
    ) {
        const temp = {
            ...options,
            ...subset,
        };
        return JSON.stringify(temp) === JSON.stringify(options);
    }
}
